<template>
  <q-dialog
    ref="dialog"
    :persistent="persistent"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-pa-sm">
      <q-card-section class="row">
        <div class="text-h6 ellipsis">
          {{ reAuthenticateTitle || $t('users.modals.reAuthenticate.title') }}
        </div>
        <q-space />
        <q-btn
          v-if="showCloseIcon"
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-card-section>
        <p>
          {{ caption || $t('users.modals.reAuthenticate.textCaption') }}
        </p>
      </q-card-section>
      <q-form @submit="onSubmitForm">
        <q-card-section class="q-pt-none">
          <q-form
            v-if="!hideEmailProvider"
            class="fit row"
          >
            <q-input
              v-model="password"
              :type="hidePassword ? 'password' : 'text'"
              :label="$t('login.inputPasswordLabel')"
              autocomplete="current-password"
              class="col-12"
            >
              <template #append>
                <q-icon
                  :name="hidePassword ? 'visibility_off' : 'visibility'"
                  class="cursor-pointer"
                  @click="hidePassword = !hidePassword"
                />
              </template>
            </q-input>
          </q-form>
        </q-card-section>

        <q-card-actions
          v-if="!hideEmailProvider"
          align="right"
        >
          <q-btn
            v-close-popup
            flat
            :label="cancelLabel || $t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('users.modals.reAuthenticate.buttonReAuthenticate')"
            :loading="authSpinner"
            type="submit"
            color="primary"
          />
        </q-card-actions>

        <q-card-actions align="center">
          <provider-authorization-buttons
            :display-google="isCapacitorMode ? true : !!(gglProvider)"
            :display-apple="isCapacitorMode ? true : !!(appleProvider)"
            :display-microsoft="false"
            :is-google-loading="googleAuthSpinner"
            :is-apple-loading="appleAuthSpinner"
            class="full-width"
            mode="re-auth"
            @google-click="onGoogleClick"
            @apple-click="onAppleClick"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { auth } from 'boot/firebase';
import ProviderAuthorizationButtons from 'components/UI/buttons/ProviderAuthorizationButtons';
import VerifyAuthenticatorAppMfa from 'components/Users/Modals/VerifyAuthenticatorAppMfa.vue';
import VerifyPhoneNumberMfa from 'components/Users/Modals/VerifyPhoneNumberMfa.vue';
import { getMultiFactorResolver } from 'firebase/auth';
import { get } from 'lodash';
import { mapActions } from 'pinia';
import { MFA_METHODS } from 'src/constants/authorization';
import Interactions from 'src/mixins/Interactions';
import SsoProvidersMixin from 'src/mixins/SsoProvidersMixin';
import { auth as authStore } from 'src/store/auth';
import { logEvent } from 'src/utils/LogEvent';

const PROVIDER_MODE = {
  // Sign out user and login it with provider using firebase's `signIn` methods
  RE_LOGIN: 're-login',
  // Reauthorize user using firebase's `user.reauthorize` methods
  RE_AUTHENTICATE: 're-auth',
};

export default {
  name: 'ReAuthenticate',

  components: { ProviderAuthorizationButtons },

  mixins: [SsoProvidersMixin, Interactions],

  props: {
    /**
     * Dialog window message
     */
    caption: {
      type: String,
      default: null,
    },

    /**
     * Hides email/password form
     */
    hideEmailProvider: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets the re-athenticate flow
     */
    providerMode: {
      type: String,
      default: PROVIDER_MODE.RE_LOGIN,
    },

    reAuthenticateTitle: {
      type: String,
      default: null,
    },

    persistent: {
      type: Boolean,
      default: false,
    },

    cancelLabel: {
      type: String,
      default: null,
    },

    showCloseIcon: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['ok', 'cancel', 'hide'],

  data() {
    return {
      authSpinner: false,
      hidePassword: true,

      password: '',
    };
  },

  methods: {
    ...mapActions(authStore, [
      'reauthenticate',
      'changeLoaderState',
    ]),

    async onSubmitForm() {
      try {
        await this.reauthenticate({ password: this.password });
        this.$emit('ok');
        this.hide();
      } catch (err) {
        if (err.code === 'auth/multi-factor-auth-required') {
          this.showVerifyMFADialog(err);
        } else {
          const errorMessage = err.code === 'auth/wrong-password'
            ? this.$t('login.notifyWrongPasswordError')
            : err.message;
          this.notifyNegative(errorMessage);
        }
      }
    }, // onSubmitForm()

    onGoogleClick() {
      return this.providerMode === PROVIDER_MODE.RE_LOGIN
        ? this.onGoogleReLogin()
        : this.onGoogleReAuthenticate();
    }, // onGoogle()

    onAppleClick() {
      return this.providerMode === PROVIDER_MODE.RE_LOGIN
        ? this.onAppleReLogin()
        : this.onAppleReAuthenticate();
    }, // onApple()

    async onGoogleReLogin() {
      const originalUserEmail = auth.currentUser.email;
      const userCredentials = await this.onGoogleLogin();

      this.handleReLoginResult(userCredentials, originalUserEmail, 'Google');
    }, // onGoogleReLogin()

    async onGoogleReAuthenticate() {
      const originalUserEmail = auth.currentUser.email;
      const userCredentials = await this.reAuthenticateWithGoogle();

      this.handleReAuthenticateResult(userCredentials, originalUserEmail, 'Google');
    }, // onGoogleReAuthenticate()

    async onAppleReLogin() {
      const originalUserEmail = auth.currentUser.email;
      const userCredentials = await this.onAppleLogin();

      this.handleReLoginResult(userCredentials, originalUserEmail, 'Apple');
    }, // onGoogleReauthenticate()

    async onAppleReAuthenticate() {
      const originalUserEmail = auth.currentUser.email;
      const userCredentials = await this.reAuthenticateWithApple();

      this.handleReAuthenticateResult(userCredentials, originalUserEmail, 'Apple');
    }, // onGoogleReAuthenticate()

    /**
     * @param {UserCredential | {cancelledRequest: boolean}} userCredentials
     * @param {string} originalUserEmail
     * @param {'Apple' | 'Google'} provider
     */
    handleReLoginResult(userCredentials, originalUserEmail, provider) {
      if (userCredentials?.cancelledRequest) {
        this.$emit('cancel');
        this.hide();
        return;
      }

      if (get(userCredentials, ['user', 'email']) === originalUserEmail) {
        this.$emit('ok');
        this.hide();
      } else {
        this.notifyNegative(this.$t('users.modals.reAuthenticate.emailIsNotMatchForMFA', { provider }), {
          timeout: 10000,
        });
        this.$emit('cancel');
        this.hide();
        this.redirectToLoginPage();
      }
    }, // handleReLoginResult()

    /**
     * @param {UserCredential} userCredentials
     * @param {string} originalUserEmail
     * @param {'Apple' | 'Google'} provider
     */
    handleReAuthenticateResult(userCredentials, originalUserEmail, provider) {
      if (userCredentials?.cancelledRequest) {
        this.$emit('cancel');
        this.hide();
        return;
      }

      if (!userCredentials) {
        this.$emit('ok', { closeParent: true });
        this.hide();
        return;
      }

      if (get(userCredentials, ['user', 'email']) === originalUserEmail) {
        this.$emit('ok');
        this.hide();
      } else {
        this.notifyNegative(this.$t('users.modals.reAuthenticate.emailIsNotMatch', { provider }), {
          timeout: 10000,
        });
        this.$emit('cancel');
        this.hide();
      }
    }, // handleReAuthenticateResult()

    showVerifyMFADialog(error) {
      const resolver = getMultiFactorResolver(auth, error);
      const factorId = get(resolver, ['hints', 0, 'factorId']);
      const MfaComponent = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? VerifyAuthenticatorAppMfa
        : VerifyPhoneNumberMfa;
      const mfaProps = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? { resolver }
        : { resolver, loginData: { email: auth.currentUser.email, password: this.password } };

      this.openComponentDialog(
        MfaComponent,
        mfaProps,
        () => {
          this.$emit('ok');
          this.hide();
        },
      );
    }, // showVerifyMFADialog()

    redirectToLoginPage() {
      try {
        this.$router.push({ name: 'login' });
      } catch (err) {
        logEvent(err, {
          context: {
            redirect_data: {
              route: this.$route,
            },
          },
        });
      }
    }, // redirectToLoginPage()

    show() {
      this.$refs.dialog.show();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      this.$emit('hide');
    },

  },
};
</script>
